import {
    API_GET_ACCOUNTS_SUCCESS,
    API_GET_APPLICATION_SUCCESS
} from '@api/lendingAPI/LendingAPI.actions';
import { tracking } from '@iwoca/frontend-tracking-library';
import {
    GetAccountsResponse,
    GetApplicationResponse
} from '@iwoca/lapi-client/edge';
import { RootState } from '@iwProvider/configureStore';
import { getCurrentLocation } from '@iwRouter/HistoryProvider';
import { getQueryParameter } from '@iwRouter/LocationHelper';
import classNames from 'classnames';
import { FormattedMessage } from 'react-intl';
import { connect } from 'react-redux';
import { Dispatch } from 'redux';

import IwocaLogo from './iwocaLogo';
import IwocaPayLogo from './iwocaPayLogo';
import MenuAction from './MenuAction';
import styles from './Navigation.module.css';
import { ProductDashboardLinks } from './ProductDashboardLinks';
import { useGetNavigationData } from './useGetNavigationData';
import { isIwocaPaySeller } from '../../api/iwocaPaySelector';
import IwMedia from '../../iwMedia/IwMedia';
import { OPEN_MODAL } from '../../iwModals/Modals.actions';
import { REQUEST_CHANGE_ROUTER } from '../../iwRouter/IwRouter.actions';
import { PhoneSVG } from '../../svgComponents/phoneSVG/index';
import { SwitchIconSVG } from '../../svgComponents/switchIconSVG/index';
import { FLEXI_12_URL } from '../dashboard/Dashboard.sagas';

const mapStateToProps = (state: RootState) => ({
    isIwocaPaySeller: isIwocaPaySeller(state),
    isAccessUser: state.siteContext.isAccessUser
});

const mapDispatchToProps = (dispatch: Dispatch) => ({
    goToHome: () =>
        dispatch({
            type: REQUEST_CHANGE_ROUTER,
            location: { pathname: FLEXI_12_URL }
        }),
    openHelpModal: () =>
        dispatch({
            type: OPEN_MODAL,
            id: 'HelpModal'
        }),
    syncAccountsResponseWithReduxStore: (
        accountsResponse: GetAccountsResponse
    ) => {
        dispatch({
            type: API_GET_ACCOUNTS_SUCCESS,
            response: accountsResponse
        });
    },
    syncApplicationResponseWithReduxStore: (
        applicationResponse: GetApplicationResponse
    ) => {
        dispatch({
            type: API_GET_APPLICATION_SUCCESS,
            response: applicationResponse
        });
    }
});

type PropsFromRedux = ReturnType<typeof mapStateToProps> &
    ReturnType<typeof mapDispatchToProps>;

const Navigation = ({
    isIwocaPaySeller,
    isAccessUser,
    openHelpModal,
    goToHome,
    syncAccountsResponseWithReduxStore,
    syncApplicationResponseWithReduxStore
}: PropsFromRedux) => {
    const { showIwocaPay, accountsResponse, applicationResponse } =
        useGetNavigationData();

    // The tabular flexi dashboard still relies on this data being in the redux store.
    // Sagas which used to fetch this data on page load have been removed mean
    // Some parts of the UI still using selectosrs try to access this data from the store
    // And the customer ends up viewing the wrong state, unless they trigger a fetch to refresh the data in some way
    if (accountsResponse) {
        syncAccountsResponseWithReduxStore(accountsResponse);
    }
    if (applicationResponse) {
        syncApplicationResponseWithReduxStore(applicationResponse);
    }

    return (
        <div className={styles.navigationWrapper}>
            <nav
                className={styles.navigationContainer}
                data-testid="navigation"
            >
                <div className={styles.actionsContainer}>
                    <div
                        className={styles.logoContainer}
                        data-testid="dashboard-navigation-logo"
                        onClick={goToHome}
                    >
                        {showIwocaPay ? (
                            <IwocaPayLogo className={styles.iwocaLogo} />
                        ) : (
                            <IwocaLogo className={styles.iwocaLogo} />
                        )}
                    </div>
                    {!isAccessUser && (
                        <IwMedia from="medium">
                            <div
                                className={styles.productLinks}
                                data-testid="product-links-desktop"
                            >
                                <ProductDashboardLinks />
                            </div>
                        </IwMedia>
                    )}
                </div>

                <div className={styles.actionsContainer}>
                    <IwMedia from="small">
                        {isIwocaPaySeller && (
                            <a
                                href={getIwocaPayDashboardLink()}
                                onClick={() => {
                                    tracking.ampli.switchedToIwocaPayDashboard();
                                }}
                                className={classNames(
                                    styles.rightAction,
                                    styles.iwocapayDashboardLink
                                )}
                            >
                                <FormattedMessage
                                    id="_.navigation.iwocapayDashboard"
                                    defaultMessage="iwocaPay Seller"
                                />
                                <SwitchIconSVG className={styles.switchIcon} />
                            </a>
                        )}
                        <button
                            className={styles.rightAction}
                            onClick={openHelpModal}
                        >
                            <FormattedMessage
                                id="_.navigation.help"
                                defaultMessage="Help"
                            />
                            <PhoneSVG className={styles.helpLogo} />
                        </button>
                    </IwMedia>
                    <MenuAction />
                </div>
            </nav>
        </div>
    );
};

export function getIwocaPayDashboardLink() {
    const routerStateKey = getQueryParameter(getCurrentLocation(), 'state_key');
    const query = routerStateKey ? `?state_key=${routerStateKey}` : '';
    return '/pay/activation/' + query;
}

// eslint-disable-next-line import/no-default-export
export default connect(mapStateToProps, mapDispatchToProps)(Navigation);
